/**
 * WinRateChart Component
 * Line chart tracking win rate trend over time — Recharts / shadcn chart (full mode)
 * plus ApexCharts mini sparkline for stat cards.
 */

import React, { useMemo, useEffect } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Area, CartesianGrid, ComposedChart, ReferenceLine, XAxis, YAxis } from "recharts";
import { useBreakpointValue, Box, Text, Spinner } from "@chakra-ui/react";
import { DashboardWidgetLoadingChart } from "../../Dashboard/components/DashboardWidgetLoadingCard";
import { getDashboardWidgetLoadingMessage } from "../../Dashboard/components/dashboardWidgetLoadingMessages";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { useWinRateTrend } from "../../../../hooks/useTradeStatistics";
import { useStatisticsWidgetErrorGate } from "../../../../hooks/useStatisticsWidgetErrorGate";
import { StatisticsInlineError } from "../../../../components/errors/StatisticsInlineError";
import { formatDateAsCalendarDayInTimeZone } from "../../../../utils/notificationPeriod";
import type { WinRateTrendResponse } from "../../../../services/tradeStatisticsApi";
import { useDisplayTimeZone } from "../../../../contexts/DisplayTimezoneContext";
import {
  formatChartHourAxisLabel,
  formatYmdBucketShortInZone,
  formatYmBucketShortInZone,
  parseStatisticsChartInstant,
  shouldIncludeYearOnChartAxis,
} from "../../../../utils/displayDateTime";
import {
  getSamplingConfig,
  downsampleDataPoints,
  hourlyChartSpansMultipleDays,
  resolveChartTimeGrain,
} from "../../../../utils/chartDataSampling";
import { filterChartPointsToLocalRange } from "../../../../utils/chartLocalRangeFilter";
import {
  STATS_FONT_FAMILY,
  useStatsCardHeadlineColor,
} from "../../../Dashboard/Statistics/components/statisticsCardHeadline";
import {
  STATS_CHART_TOOLTIP_TITLE_CLASS,
  StatisticsChartTooltipRow,
  statsChartTooltipContentClassName,
} from "../../../Dashboard/Statistics/components/statisticsChartTooltip";

function finiteNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

interface WinRateChartProps {
  brokerAccountCode: string;
  period?: "hourly" | "daily" | "monthly";
  timeframe?: import("../../../../services/tradeStatisticsApi").DashboardSummaryCardTimeframe;
  chartLocalStart?: string | null;
  chartLocalEnd?: string | null;
  startDate?: Date;
  endDate?: Date;
  localStartDate?: string;
  localEndDate?: string;
  reportingTimezone?: string | null;
  useWhiteColor?: boolean;
  fullChart?: boolean;
  onLatestCumulativeWinRateChange?: (value: number) => void;
  useExternalDataOnly?: boolean;
  data?: WinRateTrendResponse | null;
  dataLoading?: boolean;
  viewOptions?: {
    averageWinRate?: boolean;
  };
}

const COLOR_WIN_RATE = "#3EEAEC";
const COLOR_AVERAGE = "#38BBD1";

type WinRateChartDatum = {
  label: string;
  winRate: number;
};

const WinRateChart: React.FC<WinRateChartProps> = ({
  brokerAccountCode,
  period = "monthly",
  timeframe = "today",
  chartLocalStart,
  chartLocalEnd,
  startDate,
  endDate,
  localStartDate,
  localEndDate,
  reportingTimezone,
  useWhiteColor = false,
  fullChart = false,
  onLatestCumulativeWinRateChange,
  useExternalDataOnly,
  data: dataFromParent,
  dataLoading: dataLoadingFromParent,
  viewOptions,
}) => {
  const displayTimeZone = useDisplayTimeZone();
  const axisTickColor = useStatsCardHeadlineColor();

  const filters = useMemo(() => {
    const tz = reportingTimezone?.trim() || undefined;
    const hasLocal =
      typeof localStartDate === "string" &&
      localStartDate.trim() !== "" &&
      typeof localEndDate === "string" &&
      localEndDate.trim() !== ""
        ? true
        : Boolean(startDate && endDate);
    return {
      period,
      timeframe: hasLocal ? undefined : timeframe,
      ...(hasLocal
        ? {
            local_start_date:
              localStartDate?.trim() ||
              formatDateAsCalendarDayInTimeZone(startDate!, tz || "UTC"),
            local_end_date:
              localEndDate?.trim() ||
              formatDateAsCalendarDayInTimeZone(endDate!, tz || "UTC"),
          }
        : {}),
      ...(tz ? { timezone: tz } : {}),
    };
  }, [period, timeframe, startDate, endDate, localStartDate, localEndDate, reportingTimezone]);

  const skipHook = useExternalDataOnly === true;
  const { data: dataFromHook, loading: loadingFromHook, error, errorAction } = useWinRateTrend(
    brokerAccountCode,
    filters,
    { skip: skipHook }
  );

  const data = skipHook ? (dataFromParent ?? null) : dataFromHook;
  const loading = skipHook ? (dataLoadingFromParent ?? false) : loadingFromHook;
  const { showWidgetError, widgetError, widgetAction } = useStatisticsWidgetErrorGate(
    skipHook ? null : error,
    skipHook ? null : errorAction
  );

  useEffect(() => {
    if (data?.data_points && data.data_points.length > 0 && onLatestCumulativeWinRateChange) {
      const latestCumulative = data.data_points[data.data_points.length - 1]?.cumulative_win_rate || 0;
      onLatestCumulativeWinRateChange(latestCumulative);
    } else if (onLatestCumulativeWinRateChange) {
      onLatestCumulativeWinRateChange(0);
    }
  }, [data, onLatestCumulativeWinRateChange]);

  const { chartData, miniChartData, averageWinRate } = useMemo(() => {
    if (!data || !data.data_points || data.data_points.length === 0) {
      return { chartData: [] as WinRateChartDatum[], miniChartData: [] as number[], averageWinRate: 0 };
    }

    const scopedPoints = filterChartPointsToLocalRange(
      data.data_points,
      chartLocalStart,
      chartLocalEnd
    );
    if (scopedPoints.length === 0) {
      return { chartData: [] as WinRateChartDatum[], miniChartData: [] as number[], averageWinRate: 0 };
    }

    const effectiveTimeframe = startDate || endDate ? "all-time" : timeframe;
    const config = getSamplingConfig(effectiveTimeframe);
    const pointDates = scopedPoints.map((point) => point.date);
    const timeGrain = resolveChartTimeGrain({
      responseGrain: data.period,
      requestGrain: period,
      dates: pointDates,
    });
    const dailyFormat = timeGrain === "daily" || timeGrain === "weekly";
    const hourlyIncludeDay = timeGrain === "hourly" && hourlyChartSpansMultipleDays(pointDates);

    const dataPointsWithValues = scopedPoints.map((point) => ({
      date: point.date,
      value: point.period_win_rate,
    }));

    const sampled = downsampleDataPoints(dataPointsWithValues, config.maxDataPoints);

    const chartBuckets = scopedPoints.map((point) => {
      if (timeGrain === "hourly" || dailyFormat) {
        return point.date.length >= 10 ? point.date.slice(0, 10) : point.date.split("T")[0] || point.date;
      }
      return point.date.length >= 7 ? point.date.slice(0, 7) : point.date;
    });
    const includeYear =
      timeGrain !== "hourly" && dailyFormat
        ? shouldIncludeYearOnChartAxis(chartBuckets)
        : false;

    const rows: WinRateChartDatum[] = sampled.map((point: any) => {
      let label: string;
      try {
        if (timeGrain === "hourly") {
          const parsed = parseStatisticsChartInstant(point.date);
          if (parsed) {
            label = formatChartHourAxisLabel(parsed, displayTimeZone, {
              includeDay: hourlyIncludeDay,
            });
          } else {
            label = point.date;
          }
        } else if (dailyFormat) {
          const ymd =
            point.date.length >= 10 ? point.date.slice(0, 10) : point.date.split("T")[0] || point.date;
          label = formatYmdBucketShortInZone(ymd, displayTimeZone, { includeYear });
        } else {
          const ym = point.date.length >= 7 ? point.date.slice(0, 7) : point.date;
          label = formatYmBucketShortInZone(ym, displayTimeZone);
        }
      } catch {
        label = point.date;
      }
      return { label, winRate: finiteNumber(point.value) };
    });

    return {
      chartData: rows,
      miniChartData: sampled.map((p: any) => p.value),
      averageWinRate: finiteNumber(data.current_win_rate),
    };
  }, [
    data,
    period,
    startDate,
    endDate,
    timeframe,
    displayTimeZone,
    chartLocalStart,
    chartLocalEnd,
  ]);

  const chartConfig: ChartConfig = {
    winRate: { label: "Win Rate", color: COLOR_WIN_RATE },
  };

  // Mini chart color
  const color = useWhiteColor ? "#FFFFFF" : "#17FEAE";

  const miniChartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "area",
      toolbar: { show: false },
      animations: { enabled: true },
      padding: { top: 0, right: -10, bottom: 0, left: -10 },
      zoom: { enabled: false },
      selection: { enabled: false },
      dropShadow: { enabled: true, color, top: 0, left: 0, blur: 4, opacity: 0.5 },
    },
    stroke: { curve: "smooth", width: 3, colors: [color] },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: [color],
        inverseColors: false,
        opacityFrom: 0,
        opacityTo: 0,
        stops: [0, 100],
      },
      colors: [color],
    },
    colors: [color],
    dataLabels: { enabled: false },
    grid: { show: false, padding: { top: -15, right: -15, bottom: -15, left: -15 } },
    xaxis: {
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      crosshairs: { show: false },
    },
    yaxis: {
      labels: { show: false },
      min: 0,
      max: 100,
      forceNiceScale: false,
      crosshairs: { show: false },
    },
    tooltip: { enabled: false },
    legend: { show: false },
    plotOptions: { area: { fillTo: "end" } },
  }), [color]);

  const miniChartHeight = useBreakpointValue({ base: 30, sm: 38, md: 45 }) || 38;

  if (loading) {
    if (fullChart) {
      return (
        <Box w="100%" h="100%" minH={0} flex="1" position="relative">
          <DashboardWidgetLoadingChart
            ariaLabel={getDashboardWidgetLoadingMessage("winRateTrend")}
          />
        </Box>
      );
    }
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={miniChartHeight}>
        {/* @ts-ignore Chakra UI Spinner type complexity */}
        <Spinner size="sm" color={color} />
      </Box>
    );
  }

  if (error && showWidgetError && widgetError) {
    if (fullChart) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" minH="200px" w="100%" px={4}>
          <StatisticsInlineError message={widgetError} action={widgetAction ?? undefined} className="w-full max-w-lg" />
        </Box>
      );
    }
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={miniChartHeight}>
        <Text fontSize="xs" color="red.500">{widgetError}</Text>
      </Box>
    );
  }

  if (!data || !data.data_points || data.data_points.length === 0) {
    if (fullChart) {
      return (
        <Box w="100%" h="100%" minH={0} flex="1" display="flex" alignItems="center" justifyContent="center">
          <Text fontSize="sm" color="gray.500" fontFamily={STATS_FONT_FAMILY}>
            No data available for the selected timeframe
          </Text>
        </Box>
      );
    }
    return (
      <Box w="100%" h={miniChartHeight} display="flex" alignItems="center" justifyContent="center" color="gray.400">
        <Text fontSize="sm">No data available</Text>
      </Box>
    );
  }

  if (!fullChart) {
    return (
      <Box w="100%" h={miniChartHeight}>
        <ReactApexChart
          options={miniChartOptions}
          series={[{ name: "Win Rate", data: miniChartData }]}
          type="area"
          height={miniChartHeight}
          width="100%"
        />
      </Box>
    );
  }

  return (
    <Box id="win-rate-chart" w="100%" h="100%" minH={0} flex="1" position="relative">
      <ChartContainer
        config={chartConfig}
        className="h-full w-full min-h-0"
        style={{ fontFamily: STATS_FONT_FAMILY }}
      >
        <ComposedChart
          accessibilityLayer
          data={chartData}
          margin={{ top: 16, right: 24, left: 16, bottom: 12 }}
        >
          <CartesianGrid vertical={false} strokeDasharray="4 4" className="stroke-border/50" />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            width={40}
            domain={[0, 100]}
            tick={{ fill: axisTickColor, fontSize: 11 }}
            tickFormatter={(val) => `${val}%`}
          />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            minTickGap={40}
            interval="preserveStartEnd"
            tick={{ fill: axisTickColor, fontSize: 11 }}
          />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                className={statsChartTooltipContentClassName("w-[200px]")}
                labelClassName={STATS_CHART_TOOLTIP_TITLE_CLASS}
                indicator="dot"
                labelFormatter={(_value, payload) => {
                  const row = payload?.[0]?.payload as WinRateChartDatum | undefined;
                  return row?.label ?? "";
                }}
                formatter={(value) => {
                  const n = Number(value);
                  return (
                    <StatisticsChartTooltipRow
                      label="Win Rate"
                      value={`${n.toFixed(2)}%`}
                    />
                  );
                }}
              />
            }
          />
          {viewOptions?.averageWinRate && averageWinRate > 0 && (
            <ReferenceLine
              y={averageWinRate}
              stroke={COLOR_AVERAGE}
              strokeWidth={2}
              strokeDasharray="6 4"
            />
          )}
          <Area
            dataKey="winRate"
            type="monotone"
            fill="transparent"
            stroke={COLOR_WIN_RATE}
            strokeWidth={2.5}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ChartContainer>
    </Box>
  );
};

export default WinRateChart;
